import React, { useContext, useState, useEffect, useRef } from 'react'
import styles from './styles.module.css'
import { StudentsContext } from '../../../utils/context'
import Table from '../../../components/Table'
import Button from '../../../components/Button'
import Confirmation from '../../../components/Confirmation/Confirmation'
import { Input } from '../../../components/ModalInputs'
import moment from 'moment'
import Modal from './extraModal/AchieveModal'
import { useDispatch, useSelector } from 'react-redux'
import {
  getStudentFiles,
  uploadStudentFile,
  downloadStudentFile,
  deleteStudentFile,
  renameStudentFile,
} from '../../../actions/students/files'

const AGENT_URL = 'http://localhost:8967'

export default function Achieve() {
  const dispatch = useDispatch()
  const { allForms, addMode, studentTabData, updateInitialValues, trackDeletion, studentId } = useContext(StudentsContext)

  const { values, setFieldValue, setValues } = allForms
  const { achievements: achievementsData } = studentTabData || {}
  const { studentFiles } = useSelector(state => state.students.data)

  const lastSyncedRef = useRef(null)
  const fileInputRef = useRef(null)
  const downloadLinkRef = useRef(null)

  const [selectedItem, setSelectedItem] = useState(null)
  const [modalAddMode, setModalAddMode] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // file panel state
  const [selectedFile, setSelectedFile] = useState(null)
  const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(false)
  const [renameMode, setRenameMode] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [jpegQuality, setJpegQuality] = useState(60)
  const [scanners, setScanners] = useState([])
  const [selectedScanner, setSelectedScanner] = useState(null)
  const [scanning, setScanning] = useState(false)

  useEffect(() => {
    const dataJson = JSON.stringify(achievementsData)
    if (!addMode && achievementsData && lastSyncedRef.current !== dataJson) {
      lastSyncedRef.current = dataJson
      setValues(prev => ({ ...prev, achievements: [...achievementsData] }))
      if (updateInitialValues) updateInitialValues('achievements', achievementsData)
    }
  }, [achievementsData, addMode, setValues, updateInitialValues])

  useEffect(() => {
    if (!addMode && studentId) dispatch(getStudentFiles(studentId))
  }, [studentId, addMode]) // eslint-disable-line

  useEffect(() => {
    fetch(`${AGENT_URL}/scanners`)
      .then(r => r.ok ? r.json() : [])
      .then(list => { setScanners(list); if (list.length > 0) setSelectedScanner(list[0].id) })
      .catch(() => {})
  }, [])

  // --- table ---
  const items = values.achievements || []

  const handleAdd = () => { setModalAddMode(true); setSelectedItem({}) }
  const handleEdit = () => {
    if (selectedRow !== null && items[selectedRow]) { setModalAddMode(false); setSelectedItem(items[selectedRow]) }
  }
  const handleDeleteClick = () => { if (selectedRow !== null) setShowDeleteConfirm(true) }

  const handleDeleteConfirm = () => {
    const item = items[selectedRow]
    if (item?.id && !String(item.id).startsWith('new_')) trackDeletion('achievements', item.id)
    setFieldValue('achievements', items.filter((_, i) => i !== selectedRow))
    setTimeout(() => allForms.handleSubmit(), 0)
    setSelectedRow(null)
    setShowDeleteConfirm(false)
  }

  const closeModal = () => setSelectedItem(null)

  const handleSaveItem = (itemData) => {
    if (modalAddMode || !itemData.id) {
      setFieldValue('achievements', [...items, { ...itemData, id: `new_${Date.now()}` }])
    } else {
      setFieldValue('achievements', items.map(i => i.id === itemData.id ? itemData : i))
    }
    setTimeout(() => allForms.handleSubmit(), 0)
    closeModal()
    setSelectedRow(null)
  }

  // --- files ---
  const getFileName = (idx) => {
    const f = (studentFiles || [])[idx]
    return f?.file_name || f?.name || f || ''
  }

  const handleUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    dispatch(uploadStudentFile(studentId, fd))
    e.target.value = ''
  }

  const handleDownload = async () => {
    if (selectedFile === null) return
    const fileName = getFileName(selectedFile)
    const blob = await dispatch(downloadStudentFile(studentId, fileName))
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = downloadLinkRef.current
    a.href = url; a.download = fileName; a.click()
    URL.revokeObjectURL(url)
  }

  const handlePreview = async () => {
    if (selectedFile === null) return
    const blob = await dispatch(downloadStudentFile(studentId, getFileName(selectedFile)))
    if (!blob) return
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const handleFileDeleteConfirm = () => {
    dispatch(deleteStudentFile(studentId, getFileName(selectedFile)))
    setSelectedFile(null)
    setShowFileDeleteConfirm(false)
  }

  const handleRenameClick = () => { setNewFileName(getFileName(selectedFile)); setRenameMode(true) }

  const handleRenameSave = () => {
    if (!newFileName.trim()) return
    dispatch(renameStudentFile(studentId, getFileName(selectedFile), newFileName.trim()))
    setRenameMode(false)
    setSelectedFile(null)
  }

  const handleScan = async () => {
    setScanning(true)
    try {
      const res = await fetch(`${AGENT_URL}/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: selectedScanner, dpi: 200, format: 'jpeg', quality: Number(jpegQuality) || 80 })
      })
      if (!res.ok) { alert('Ошибка сканирования'); return }
      const blob = await res.blob()
      const cd = res.headers.get('content-disposition') || ''
      const match = cd.match(/filename="?([^"]+)"?/)
      const fileName = match ? match[1] : `scan_${Date.now()}.jpg`
      const fd = new FormData()
      fd.append('file', new File([blob], fileName, { type: blob.type }))
      dispatch(uploadStudentFile(studentId, fd))
    } catch { alert('Агент сканера недоступен') }
    finally { setScanning(false) }
  }

  return (
    <div className={styles.tabWrapper}>
      <input ref={fileInputRef} type='file' hidden onChange={handleUpload} />
      <a ref={downloadLinkRef} hidden />

      {selectedItem !== null && (
        <Modal
          closeModal={closeModal}
          data={selectedItem}
          height='430px'
          addMode={modalAddMode}
          tabType='achievements'
          onSave={handleSaveItem}
        />
      )}

      {showDeleteConfirm && (
        <Confirmation
          title='Вы уверены, что хотите удалить достижение?'
          acceptConfirmation={handleDeleteConfirm}
          rejectConfirmation={() => setShowDeleteConfirm(false)}
        />
      )}

      {showFileDeleteConfirm && (
        <Confirmation
          title='Вы уверены, что хотите удалить файл?'
          acceptConfirmation={handleFileDeleteConfirm}
          rejectConfirmation={() => setShowFileDeleteConfirm(false)}
        />
      )}

      <div className={styles.achieveContainer}>
        {/* левая часть — таблица */}
        <div className={styles.achieveLeft}>
          <Table className={styles.table}>
            <thead>
              <tr>
                <th>Достижения</th>
                <th>Тип</th>
                <th>Дата</th>
                <th>Приказ</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr
                  key={item.id || idx}
                  onClick={() => setSelectedRow(idx)}
                  style={{ cursor: 'pointer', backgroundColor: selectedRow === idx ? '#DEECFF' : 'transparent' }}
                >
                  <td>{item.achievement || ''}</td>
                  <td>{item.type_id || ''}</td>
                  <td>{item.date ? moment(item.date).format('DD.MM.YYYY') : ''}</td>
                  <td>{item.order_number || ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className={styles.inputsRow} style={{ marginTop: '0.5rem' }}>
            <Button mode='bordered' onClick={handleAdd}>Добавить</Button>
            <Button mode='bordered' onClick={handleEdit} disabled={selectedRow === null}>Изменить</Button>
            <Button mode='bordered' onClick={handleDeleteClick} disabled={selectedRow === null}>Удалить</Button>
          </div>
        </div>

        {/* правая часть — файлы */}
        <div className={styles.achieveRight}>
          <div className={styles.filePanel}>
            <div className={styles.filesList}>
              {(studentFiles || []).map((file, idx) => {
                const name = file?.file_name || file?.name || file
                return (
                  <div
                    key={name || idx}
                    onClick={() => { setSelectedFile(idx); setRenameMode(false) }}
                    className={`${styles.fileItem} ${selectedFile === idx ? styles.fileItemSelected : ''}`}
                  >
                    {name}
                  </div>
                )
              })}
            </div>
            <div className={styles.filesButtons}>
              <Button mode='bordered' onClick={() => fileInputRef.current.click()}>+ Добавить</Button>
              <Button mode='bordered' onClick={handleDownload} disabled={selectedFile === null}>Получить</Button>
              <Button mode='bordered' onClick={handlePreview} disabled={selectedFile === null}>Просмотр</Button>
              <Button mode='bordered' onClick={() => setShowFileDeleteConfirm(true)} disabled={selectedFile === null}>Удалить</Button>
              <Button mode='bordered' onClick={handleRenameClick} disabled={selectedFile === null}>Переименовать</Button>
            </div>
          </div>

          {renameMode && (
            <div className={styles.renameRow}>
              <Input label='Новое имя' value={newFileName} onChange={e => setNewFileName(e.target.value)} style={{ width: '220px' }} />
              <Button mode='bordered' onClick={handleRenameSave}>Сохранить</Button>
              <Button mode='bordered' onClick={() => setRenameMode(false)}>Отмена</Button>
            </div>
          )}

          <div className={styles.fileBottomRow}>
            <Input label='Качество JPEG' type='number' value={jpegQuality} onChange={e => setJpegQuality(e.target.value)} style={{ width: '60px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '12px' }}>Сканер</span>
              <select value={selectedScanner || ''} onChange={e => setSelectedScanner(e.target.value)} style={{ height: '28px', minWidth: '160px' }}>
                {scanners.length === 0
                  ? <option value=''>—</option>
                  : scanners.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                }
              </select>
            </div>
            <Button mode='bordered' onClick={handleScan} disabled={scanning || scanners.length === 0}>
              {scanning ? 'Сканирование...' : '+ Сканировать'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
